import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  BookOpen,
  FileQuestion,
  Award,
  Plus,
  ArrowRight,
  Edit,
  Eye,
} from 'lucide-react';

const ContentManagerDashboard = () => {
  const { user, api } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentCourses, setRecentCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, coursesRes] = await Promise.all([
        api.get('/settings/dashboard-stats'),
        api.get('/courses?limit=5'),
      ]);
      setStats(statsRes.data);
      setRecentCourses(coursesRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Courses',
      value: stats?.total_courses || 0,
      icon: BookOpen,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    {
      title: 'Published',
      value: stats?.published_courses || 0,
      icon: Eye,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
    },
    {
      title: 'Drafts',
      value: stats?.draft_courses || 0,
      icon: Edit,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100',
    },
    {
      title: 'Questions',
      value: stats?.total_questions || 0,
      icon: FileQuestion,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
    },
  ];

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-yellow-100 text-yellow-800',
      published: 'bg-green-100 text-green-800',
      archived: 'bg-slate-100 text-slate-800',
    };
    return styles[status] || 'bg-slate-100 text-slate-800';
  };

  if (loading) {
    return (
      <DashboardLayout requiredRoles={['content_manager']}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#20acf6]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout requiredRoles={['content_manager']}>
      <div className="space-y-8" data-testid="cms-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#2a3e5d]">
              Content Manager Dashboard
            </h1>
            <p className="text-slate-600 mt-1">
              Create and manage learning content
            </p>
          </div>
          <div className="flex gap-3">
            <Button asChild variant="outline">
              <Link to="/cms/questions">
                <FileQuestion className="mr-2 h-4 w-4" />
                Question Bank
              </Link>
            </Button>
            <Button asChild className="bg-[#20acf6] hover:bg-[#3bb8f7]">
              <Link to="/cms/courses/new">
                <Plus className="mr-2 h-4 w-4" />
                New Course
              </Link>
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className="border-slate-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">{stat.title}</p>
                      <p className="text-2xl font-bold text-slate-900 mt-1">
                        {stat.value}
                      </p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl ${stat.bgColor} flex items-center justify-center`}>
                      <Icon className={`w-6 h-6 ${stat.color}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Recent Courses */}
        <Card className="border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold">Recent Courses</CardTitle>
            <Button variant="ghost" asChild>
              <Link to="/cms/courses">
                View all <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {recentCourses.length === 0 ? (
              <div className="text-center py-8">
                <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600 mb-4">No courses created yet.</p>
                <Button asChild className="bg-[#20acf6] hover:bg-[#3bb8f7]">
                  <Link to="/cms/courses/new">Create Your First Course</Link>
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {recentCourses.map((course) => (
                  <div
                    key={course.id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-xl"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-12 rounded-lg overflow-hidden bg-slate-200">
                        <img
                          src={course.thumbnail_url || 'https://images.unsplash.com/photo-1762341122735-d6ae18da4c01?crop=entropy&cs=srgb&fm=jpg&w=200'}
                          alt={course.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <h4 className="font-medium text-slate-900">{course.title}</h4>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={getStatusBadge(course.status)}>
                            {course.status}
                          </Badge>
                          <span className="text-sm text-slate-500">
                            {course.module_count || 0} modules
                          </span>
                        </div>
                      </div>
                    </div>
                    <Button asChild variant="outline" size="sm">
                      <Link to={`/cms/courses/${course.id}/edit`}>
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Link>
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="border-slate-200 hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <BookOpen className="h-8 w-8 text-[#20acf6] mb-4" />
              <h3 className="font-semibold text-slate-900 mb-2">Create Course</h3>
              <p className="text-sm text-slate-600 mb-4">
                Build a new training course with modules and elements
              </p>
              <Button asChild variant="outline" className="w-full">
                <Link to="/cms/courses/new">
                  Create Course <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="border-slate-200 hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <FileQuestion className="h-8 w-8 text-[#20acf6] mb-4" />
              <h3 className="font-semibold text-slate-900 mb-2">Question Bank</h3>
              <p className="text-sm text-slate-600 mb-4">
                Create and manage reusable quiz questions
              </p>
              <Button asChild variant="outline" className="w-full">
                <Link to="/cms/questions">
                  Manage Questions <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="border-slate-200 hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <Award className="h-8 w-8 text-[#20acf6] mb-4" />
              <h3 className="font-semibold text-slate-900 mb-2">Certificate Templates</h3>
              <p className="text-sm text-slate-600 mb-4">
                Design certificate templates for courses
              </p>
              <Button asChild variant="outline" className="w-full">
                <Link to="/cms/certificates">
                  Manage Templates <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ContentManagerDashboard;
